import { Link } from "react-router-dom";
import {
  FileSearch,
  Scale,
  Building,
  Factory,
  Gem,
  Gavel,
  HardHat,
  ShieldHalf,
  ArrowUpRight,
} from "lucide-react";

const SERVICES = [
  {
    icon: FileSearch,
    slug: "claims-reports",
    image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&q=70&auto=format&fit=crop",
    title: "Pre-Loss Property Reports",
    desc:
      "Comprehensive documentation designed to establish the condition, exposure, and risk profile of a property before damage, dispute, or litigation occurs.",
  },
  {
    icon: Scale,
    slug: "legal-documentation",
    image: "https://images.unsplash.com/photo-1505664194779-8beaceb93744?w=800&q=70&auto=format&fit=crop",
    title: "Litigation-Ready Risk Documentation",
    desc:
      "Detailed, organized, defensible documentation created to support attorneys, boards, owners, and stakeholders when claims or disputes arise.",
  },
  {
    icon: Building,
    slug: "hoa-condominium",
    image: "https://images.unsplash.com/photo-1493809842364-78817add7ffb?w=800&q=70&auto=format&fit=crop",
    title: "HOA & Condominium Risk Consulting",
    desc:
      "Strategic consulting for communities managing shared assets, insurance exposure, maintenance concerns, and board-level risk decisions.",
  },
  {
    icon: Factory,
    slug: "commercial-property",
    image: "https://images.unsplash.com/photo-1577760258779-e787a1733016?w=800&q=70&auto=format&fit=crop",
    title: "Commercial Property Protection",
    desc:
      "Risk intelligence for commercial owners who need clarity, documentation, and preparation before liability becomes costly.",
  },
  {
    icon: Gem,
    slug: "luxury-residential",
    image: "https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=800&q=70&auto=format&fit=crop",
    title: "Luxury Homeowner Protection",
    desc:
      "Specialized pre-loss reporting for high-value homeowners with properties valued at $1.5 million and above.",
  },
  {
    icon: Gavel,
    slug: "legal-documentation",
    to: "/services#attorneys",
    image: "https://images.unsplash.com/photo-1521587760476-6c12a4b040da?w=800&q=70&auto=format&fit=crop",
    title: "Attorney Support & Legal Strategy",
    desc:
      "Documentation and consulting support designed for legal professionals operating in insurance disputes, property litigation, and risk management.",
  },
  {
    icon: HardHat,
    slug: "property-inspection",
    image: "https://images.unsplash.com/photo-1632759145351-1d592919f522?w=800&q=70&auto=format&fit=crop",
    title: "Property Inspection & Field Documentation",
    desc:
      "Systematic, time-stamped field documentation of roofs, envelopes, and systems — a chain of evidence, not a checklist.",
  },
  {
    icon: ShieldHalf,
    slug: "business-operations",
    image: "https://images.unsplash.com/photo-1759661881353-5b9cc55e1cf4?w=800&q=70&auto=format&fit=crop",
    title: "Cybersecurity & AI Operations Review",
    desc:
      "A business operations review covering cyber exposure, records security, and safe, practical AI implementation.",
  },
];

export const Services = ({ preview = false }) => {
  return (
    <section id="services" className="relative py-20 sm:py-24 md:py-36 btr-bg-charcoal" data-testid="services-section">
      <div className="absolute inset-0 btr-grid-animated opacity-40 pointer-events-none" />
      <div className="btr-beam" style={{ width: 500, height: 500, top: "20%", right: "-15%", background: "#a2020b", opacity: 0.16 }} />
      <div className="relative max-w-7xl mx-auto px-6">
        <div className="btr-reveal max-w-3xl">
          <span className="btr-badge">
            <span className="dot" />
            Services
          </span>
          <h2 className="font-display font-extrabold text-white text-3xl sm:text-4xl lg:text-6xl mt-6 leading-[1.05] sm:leading-[1.02] tracking-tight">
            Strategic Risk Management Services
          </h2>
          <p className="font-body text-zinc-400 text-base sm:text-lg mt-6 leading-relaxed">
            Pre-loss intelligence engineered for the highest-stakes properties, communities, and legal teams.
            Every engagement is built for clarity, defensibility, and executive decision-making.
          </p>
        </div>

        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-5 mt-14">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <Link
                key={`${s.title}-${i}`}
                to={s.to || `/topics/${s.slug}`}
                className="btr-reveal btr-service-card btr-topic-card group bg-[#0f0f10] border border-white/8 rounded-lg overflow-hidden block cursor-pointer"
                style={{ transitionDelay: `${i * 50}ms` }}
                data-testid={`service-card-${i}`}
                aria-label={`Open ${s.title} details`}
              >
                {/* Image banner */}
                <div className="relative h-36 overflow-hidden">
                  <img
                    src={s.image}
                    alt={s.title}
                    loading="lazy"
                    decoding="async"
                    width="800"
                    height="450"
                    className="absolute inset-0 w-full h-full object-cover grayscale-[0.35] contrast-[1.05] transition-transform duration-[1400ms] group-hover:scale-[1.08]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0f0f10]/40 to-[#0f0f10]" />
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(162,2,11,0.45),transparent_60%)] mix-blend-screen opacity-70 group-hover:opacity-100 transition-opacity" />
                  <div className="absolute top-3 left-3 w-8 h-8 border-t border-l border-[#a2020b]/60 group-hover:w-12 group-hover:h-12 transition-all" />
                  <div className="absolute top-3 right-3 text-[10px] font-display tracking-[0.25em] text-white/70 group-hover:text-[#a2020b] transition-colors">
                    0{i + 1}
                  </div>

                  {/* View Details pill — visible on touch, animates on hover */}
                  <div className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 bg-[#a2020b] rounded-full px-3 py-1.5 text-[10px] font-display font-bold uppercase tracking-[0.2em] text-white opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-all translate-x-0 sm:translate-x-2 sm:group-hover:translate-x-0">
                    View Details
                    <ArrowUpRight className="w-3 h-3" />
                  </div>
                </div>

                <div className="relative p-7 pt-5">
                  <div className="relative w-11 h-11 rounded-md bg-[#a2020b]/12 border border-[#a2020b]/35 flex items-center justify-center -mt-11 mb-5 btr-red-glow">
                    <Icon className="w-5 h-5 text-[#a2020b]" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display font-bold text-white text-xl leading-snug tracking-tight">
                    {s.title}
                  </h3>
                  <p className="font-body text-zinc-400 text-sm mt-3 leading-relaxed">{s.desc}</p>
                  <div className="mt-6 pt-5 border-t border-white/5 flex items-center justify-between gap-2 text-[11px] font-display tracking-[0.2em] uppercase text-zinc-500 group-hover:text-[#a2020b] transition-colors">
                    <span className="inline-flex items-center gap-2">Learn more <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" /></span>
                  </div>
                </div>
                <span className="btr-card-line" />

                {/* Border glow ring */}
                <span className="absolute inset-0 rounded-lg pointer-events-none ring-0 group-hover:ring-1 ring-[#a2020b]/50 group-hover:shadow-[0_0_40px_rgba(162,2,11,0.45)] transition-all" />
              </Link>
            );
          })}
        </div>
        {preview && (
          <div className="btr-reveal mt-12 flex justify-center">
            <Link to="/services" className="btr-btn-ghost" data-testid="view-all-services-btn">
              View All Services <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};
